import { useState, useEffect } from 'react';
import styles from './Portfolio.module.sass';
import type { RootState } from 'redux/store';
import { useSelector, useDispatch } from 'react-redux';
import PortfolioDonut from 'components/PortfolioDonut';
import PortfolioHealth from 'components/PortfolioHealth';
import type { MetricType } from 'components/PortfolioHealth';
import { 
  getPortfolio,
  getAssetCorrelations,
  getPortfolioBenchmarkComparison,
  getMaxDrawDown
} from 'services/portfolio';
import Spinner from 'components/Spinner';
import { useAuth } from 'react-oidc-context';
import HorizontalWallet from '../../../components/HorizontalWallet';

export interface HoldingData {
  portfolio_percentage: number;
  wallet_address: string;
  balance: string;
  balance_formatted: string;
  chain: string;
  decimals: number;
  logo: string;
  name: string;
  symbol: string;
  usd_value: number;
  [key: string]: any;
}

interface PortfolioStats {
  high: string;
  low: string;
  cumulativeReturn: string;
  volatility: string;
  maxDrawdown: string;
  breakeven: string;
  avgDrawdown: string;
  drawdownFreq: string;
  daysInDrawdown: string;
}

const formatCurrency = (value: number): string => {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

const MetricRow = ({ label, value, tooltip }: { label: string; value: string; tooltip?: boolean }) => {
  const formatValue = (val: string) => {
    if (val === 'None' || val === '∅') return '∅';
    if (val.includes('$')) return val;  // Don't modify currency values
    if (val.includes('m')) return val.replace('m', 'ᴍ');
    if (val.includes('%')) return val.replace('%', '﹪');
    return val;
  };
  
  const getTooltipText = (metricLabel: string) => {
    const tooltips: Record<string, string> = {
      "High": "Highest portfolio value during selected time period",
      "Low": "Lowest portfolio value during selected time period",
      "Days Down": "Percentage of days with negative returns",
      "Cumulative": "Total return over the selected time period",
      "Volatility": "Annualized standard deviation of daily returns",
      "Max Draw.": "Maximum peak-to-trough decline over selected period",
      "Avg Draw.": "Average of all drawdowns in the period",
      "Draw. Freq": "Number of drawdown events in the time period",
      "Breakeven": "Return needed to recover from maximum drawdown"
    };
    
    return tooltips[metricLabel] || "No description available";
  };

  return (
    <div className={styles.metricRow}>
      <div className={styles.metricLabel}>
        {label}
        {tooltip && (
          <span>
            ⓘ
            <div className={styles.tooltip}>{getTooltipText(label)}</div>
          </span>
        )}
      </div>
      <div 
        className={styles.metricValue}
        style={{
          fontFamily: 'monospace',
          letterSpacing: '0.05em',
          background: 'linear-gradient(180deg, #73F6FF 0%, #00BFFF 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textShadow: '0 0 8px rgba(115, 246, 255, 0.5)'
        }}
      >
        {formatValue(value)}
      </div>
    </div>
  );
};

const Portfolio = () => {
  const auth = useAuth();
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('portfolio');
  const [activeView, setActiveView] = useState<'metrics' | 'holdings'>('metrics');
  const [isMobile, setIsMobile] = useState(false);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [portfolioStats, setPortfolioStats] = useState<PortfolioStats>({
    high: '∅',
    low: '∅',
    cumulativeReturn: '∅',
    volatility: '∅',
    maxDrawdown: '∅',
    breakeven: '∅',
    avgDrawdown: '∅',
    drawdownFreq: '∅',
    daysInDrawdown: '∅'
  });
  
  const dispatch = useDispatch();
  
  const holdings = useSelector((state: RootState) => state.portfolio.holdings);
  const isLoading = useSelector((state: RootState) => state.portfolio.loading.holdings);
  const selectedWallet = useSelector((state: RootState) => 
    state.user.wallets.length > 0 ? state.user.wallets[0] : null
  );
  const selectedDateDuration = useSelector((state: RootState) => state.user.dataDuration);
  const selectedBenchmark = useSelector((state: RootState) => 
    state.user.selectedBenchmark || 'ETH'
  );

  const chartData = holdings?.map(holding => ({
    symbol: holding.symbol,
    portfolio_percentage: holding.portfolio_percentage,
    usd_value: holding.usd_value
  })) || [];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadPortfolio = async () => {
      try {
        // Remove the dispatch since getPortfolio handles dispatching internally
        await getPortfolio(auth.user.access_token);
        if (selectedDateDuration?.id) {
          await loadMetrics();
        }
      } catch (error) {
        console.error('Error loading portfolio');
      }
    };
    if (selectedWallet && (!holdings || holdings.length === 0)) {
      loadPortfolio();
    }
  }, [selectedWallet, holdings, selectedDateDuration, dispatch, auth.user.access_token]);

  const loadMetrics = async () => {
    if (!selectedDateDuration?.id || !selectedWallet) return;
    
    setIsLoadingMetrics(true);
    try {
      // const correlationsData = await getAssetCorrelations(
      //   auth.user.access_token,
      //   'ETH'
      // );

      const benchmarkData = await getPortfolioBenchmarkComparison(
        auth.user.access_token,
        selectedDateDuration.id,
        undefined,  // rf parameter
        selectedBenchmark  // add this for benchmark
      );

      const drawdownData = await getMaxDrawDown(
        auth.user.access_token,
        selectedBenchmark
      );
  
      // Handle drawdown data
      if (drawdownData) {
        setPortfolioStats(prev => ({
          ...prev,
          maxDrawdown: formatPercentage(drawdownData.max_drawdown * 100),
          breakeven: formatPercentage(drawdownData.return_to_breakeven || 0),
          avgDrawdown: formatPercentage(drawdownData.avg_drawdown),
          drawdownFreq: drawdownData.drawdown_frequency.toString(),
          daysInDrawdown: formatPercentage(drawdownData.percentage_days_in_drawdown)
        }));
      }
  
      // Handle benchmark data
      if (benchmarkData?.Portfolio?.values) {
        const values = benchmarkData.Portfolio.values.map(v => v.quote);
        
        setPortfolioStats(prev => ({
          ...prev,
          high: formatCurrency(Math.max(...values)),
          low: formatCurrency(Math.min(...values)),
          cumulativeReturn: formatPercentage(
            ((benchmarkData.Portfolio.end_value - benchmarkData.Portfolio.start_value) / 
            benchmarkData.Portfolio.start_value) * 100
          ),
          volatility: formatPercentage(
            Math.sqrt(
              values.slice(1)
                .map((value, i) => (value - values[i]) / values[i])
                .reduce((sum, ret) => sum + Math.pow(ret, 2), 0) / (values.length - 1)
            ) * Math.sqrt(365) * 100
          )
        }));
      }
  
    } catch (error) {
      console.error('Error in loadMetrics');
      setPortfolioStats({
        high: '∅',
        low: '∅',
        cumulativeReturn: '∅',
        volatility: '∅',
        maxDrawdown: '∅',
        breakeven: '∅',
        avgDrawdown: '∅',
        drawdownFreq: '∅',
        daysInDrawdown: '∅'
      });
    } finally {
      setIsLoadingMetrics(false);
    }
  };

  useEffect(() => {
    if (selectedWallet && selectedDateDuration) {
      loadMetrics();
    }
  }, [selectedWallet, selectedDateDuration, selectedBenchmark]);

  const renderMetricSections = () => {
    if (isLoadingMetrics) {
      return (
        <div className={styles.loadingContainer}>
          <Spinner />
        </div>
      );
    }

    return (
      <div className={styles.metricsContainer}>
        <div className={styles.metricSection}>
          <div className={styles.metricSectionHeader}>Portfolio Statistics</div>
          <MetricRow label="High" value={portfolioStats.high} tooltip={true} />
          <MetricRow label="Low" value={portfolioStats.low} tooltip={true} />
          <MetricRow label="Days Down" value={portfolioStats.daysInDrawdown} tooltip={true} />
        </div>

        <div className={styles.metricSection}>
          <div className={styles.metricSectionHeader}>Return Analysis</div>
          <MetricRow label="Cumulative" value={portfolioStats.cumulativeReturn} tooltip={true} />
          <MetricRow label="Volatility" value={portfolioStats.volatility} tooltip={true} />
        </div>

        <div className={styles.metricSection}>
          <div className={styles.metricSectionHeader}>Risk Metrics</div>
          <MetricRow label="Max Draw." value={portfolioStats.maxDrawdown} tooltip={true} />
          <MetricRow label="Avg Draw." value={portfolioStats.avgDrawdown} tooltip={true} />
          <MetricRow label="Draw. Freq" value={portfolioStats.drawdownFreq} tooltip={true} />
          <MetricRow label="Breakeven" value={portfolioStats.breakeven} tooltip={true} />
        </div>
      </div>
    );
  };

  // if (isLoading) {
  //   return (
  //     <div className={styles.loadingContainer}>
  //       <Spinner/>
  //     </div>
  //   );
  // }

//   return (
//     <div className={styles.container}>
//       <HorizontalWallet />
//       <div className={styles.content}>
//         <div className={`${styles.contentSplit} ${isMobile ? styles.mobile : ''}`}>
//           <div className={`${styles.sidebar} ${isMobile ? styles.mobileSidebar : ''}`}>
//             <div>
//               <h2 className={styles.sectionTitle}>Holdings</h2>
//               <PortfolioDonut 
//                 holdings={chartData}
//                 size={isMobile ? "max-w-[160px]" : "max-w-[200px]"}
//               />
//             </div>
//             {renderMetricSections()}
//           </div>
// 
//           <div className={styles.mainContent}>
//             <PortfolioHealth 
//               selectedMetric={selectedMetric}
//               onMetricSelect={setSelectedMetric}
//               activeView={activeView}
//               onViewChange={setActiveView}
//               holdings={holdings}
//             />
//           </div>
//         </div>
//       </div>
//     </div>
//   );

  return (
    <div className={styles.container}>
      {/* <HorizontalWallet /> */}
      <HorizontalWallet showWallets={false} />
      <div className={styles.content}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spinner/>
          </div>
        ) : (
          <div className={`${styles.contentSplit} ${isMobile ? styles.mobile : ''}`}>
            <div className={`${styles.sidebar} ${isMobile ? styles.mobileSidebar : ''}`}>
              <div>
                <h2 className={styles.sectionTitle}>Holdings</h2>
                <PortfolioDonut 
                  holdings={chartData}
                  size={isMobile ? "max-w-[160px]" : "max-w-[200px]"}
                />
              </div>
              {renderMetricSections()}
            </div>
            <div className={styles.mainContent}>
              <PortfolioHealth 
                selectedMetric={selectedMetric}
                onMetricSelect={setSelectedMetric}
                activeView={activeView}
                onViewChange={setActiveView}
                holdings={holdings}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Portfolio;